import { View, Text, Svg, Text as SvgText } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot, PdfHeatmap } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";
import { TABLE_BODY_LINE_HEIGHT_PX, TABLE_OUTER_PAD_FACTOR_PDF, TABLE_ROW_HEIGHT_PX } from "../../Utils/tableStyles";

// Values mirror the live Table2DHeatmap (DashboardAlt.css) so the PDF matches the
// platform 1:1 — font size, weights, colors, and FIXED cell dimensions in px,
// scaled to PDF points. Cells are NOT compressed to fit: the heatmap truncates at
// the same column the platform's viewport does (whole cells), since both use the
// same cell size and width.
const FONT_PX = 14; // matches antd table default + live heatmap
const LABEL_COLOR = "#1f1f1f"; // ≈ rgba(0,0,0,0.88)
const PINNED_COLOR = "#737373"; // ≈ rgba(0,0,0,0.55)
const CELL_W_PX = 56; // labelled cell min-width
const CELL_H_PX = 28; // labelled cell height
const CELL_SIDE_COMPACT_PX = 20; // compact cell is a 20×20 square
const GAP_PX = 1;
const ROW_LABEL_PAD_PX = 10;

export function renderTable2D(
  snapshot: Extract<PdfWidgetSnapshot, { type: "table_2d" }>,
  rect: PdfRect,
  scale: number,
) {
  // Both heatmap and plain modes render from the same grid (the plain snapshot sets
  // `plain: true`); this reuses the column-fit/truncation/clip logic for both.
  if (snapshot.heatmap) {
    return renderHeatmap(snapshot.heatmap, snapshot.title, snapshot.subtitle, rect, scale);
  }
  return null;
}

function renderHeatmap(heatmap: PdfHeatmap, title: string | null, subtitle: string | undefined, rect: PdfRect, scale: number) {
  const plain = heatmap.plain ?? false;
  const isCompact = heatmap.compact && !plain; // plain always shows labels (horizontal headers)
  const pad = Math.max(2, TABLE_OUTER_PAD_FACTOR_PDF * scale);
  const font = FONT_PX * scale;
  const gap = (plain ? 0 : GAP_PX) * scale; // plain cells are adjacent with row separators
  // Plain rows match the regular table's body row height, grown by one line per
  // extra line a wrapped answer label needs; heatmap keeps its own.
  const plainRowH = TABLE_ROW_HEIGHT_PX + 0.5 + (Math.max(1, heatmap.rowLines ?? 1) - 1) * TABLE_BODY_LINE_HEIGHT_PX;
  const cellH = isCompact
    ? CELL_SIDE_COMPACT_PX * scale
    : (plain ? plainRowH : CELL_H_PX) * scale;
  // Title font is an absolute 10pt (matching other widgets), so its row must be an
  // absolute height — a scaled height collapses below the font and clips the text.
  const titleH = 14;
  const resolvedTitle = title || "2D Table";

  const longestRowLabel = heatmap.rows.reduce((m, r) => Math.max(m, r.label.length), 0);
  const longestColLabel = heatmap.cols.reduce((m, c) => Math.max(m, c.label.length), 0);

  const contentWidth = rect.width - 2 * pad;
  const labelColWidth = Math.min(
    rect.width * 0.4,
    longestRowLabel * font * 0.58 + ROW_LABEL_PAD_PX * 2 * scale,
  );

  // Compact: uniform 20px squares (vertical headers). Labelled: each column sizes
  // to fit its header name (like the platform's content-sized columns).
  // Plain headers wrap (like the antd table), so size columns to the longest WORD,
  // not the full label — giving narrow columns matching the platform. Heatmap
  // headers stay single-line, sized to the full label.
  // Prefer the width measured in the platform font at export time (exact column
  // count); fall back to a per-character estimate when unavailable.
  const longestWord = (s: string) => s.split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0);
  const labelPad = (plain ? 22 : 6) * scale;
  const colWidths = heatmap.cols.map((c) =>
    isCompact
      ? CELL_SIDE_COMPACT_PX * scale
      : c.width != null
        ? c.width * scale
        : Math.max(CELL_W_PX * scale, (plain ? longestWord(c.label) : c.label.length) * font * 0.58 + labelPad),
  );
  // Cumulative left offset for each column (within the cells area, gap-separated).
  const colLefts: number[] = [];
  let acc = 0;
  for (const w of colWidths) {
    colLefts.push(acc);
    acc += w + gap;
  }

  // Render every column that STARTS within the viewport (including the one
  // straddling the right edge), then let overflow:hidden clip it — mirroring the
  // platform scroll, which shows a partial column at the cut-off.
  const cellsAreaW = contentWidth - labelColWidth;
  let visibleColCount = 0;
  for (let i = 0; i < colWidths.length; i++) {
    if (colLefts[i] >= cellsAreaW) break;
    visibleColCount++;
  }
  const cols = heatmap.cols.slice(0, visibleColCount);

  // Plain multi-word headers (e.g. player names) wrap to ~2 lines; single-word
  // headers (e.g. dates) stay one line like the ordinary table.
  const headerWraps = plain && heatmap.cols.some((c) => c.label.includes(" "));
  const horizontalHeaderH = (headerWraps ? font * 2.6 : font * 1.35) + 5 * scale;
  // Rotated names read bottom-up with the first letter at the band's bottom edge;
  // lift the text by a character so it isn't clipped into the cells. The 0.62/char
  // length reservation already over-estimates, so this uses that existing slack
  // rather than growing the band (which would cost a body row).
  const colHeadBottomPad = font;
  const verticalHeaderH = Math.min(rect.height * 0.55, longestColLabel * font * 0.62 + 6 * scale);
  const headerH = isCompact ? verticalHeaderH : horizontalHeaderH;

  // Render every row that STARTS within the body area (including the one straddling
  // the bottom edge), then clip — mirroring the platform's partial last row on scroll.
  const availH = rect.height - 2 * pad - titleH - headerH;
  const rowPitch = cellH + gap;
  let visibleRowCount = 0;
  for (let i = 0; i < heatmap.rows.length; i++) {
    if (i * rowPitch >= availH) break;
    visibleRowCount++;
  }
  const visibleRows = heatmap.rows.slice(0, visibleRowCount);

  return (
    <View
      style={{
        position: "absolute",
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        padding: pad,
        flexDirection: "column",
        borderWidth: 1,
        borderColor: "#e8e8e8",
        borderRadius: 4,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Title + grey date-range/subject subtitle */}
      <View style={{ height: titleH, flexShrink: 0, flexDirection: "row", alignItems: "center", paddingHorizontal: 4 * scale }}>
        <Text style={{ fontFamily: "Roboto", fontSize: 10, fontWeight: 600, color: "#1a1a1a", flex: 1 }}>{resolvedTitle}</Text>
        {subtitle ? (
          <Text style={{ fontFamily: "Roboto", fontSize: 7, color: "rgba(0,0,0,0.55)", textAlign: "right" }}>{subtitle}</Text>
        ) : null}
      </View>

      {/* Header band */}
      {isCompact ? (
        <Svg width={contentWidth} height={headerH} style={{ height: headerH, flexShrink: 0 }}>
          {cols.map((c, ci) => {
            const cx = labelColWidth + colLefts[ci] + colWidths[ci] / 2 + font * 0.35;
            const cy = headerH - colHeadBottomPad;
            return (
              <SvgText
                key={ci}
                x={cx}
                y={cy}
                transform={`rotate(-90, ${cx}, ${cy})`}
                textAnchor="start"
                fill={c.pinned ? PINNED_COLOR : LABEL_COLOR}
                style={{ fontSize: font, fontWeight: 400, fontFamily: "Roboto" }}
              >
                {c.label}
              </SvgText>
            );
          })}
        </Svg>
      ) : (
        <View
          style={{
            flexDirection: "row",
            height: headerH,
            flexShrink: 0,
            alignItems: "flex-end",
            overflow: "hidden",
            ...(plain ? { backgroundColor: "#fafafa", borderBottomWidth: 1, borderBottomColor: "#e8e8e8" } : {}),
          }}
        >
          <View style={{ width: labelColWidth }} />
          {cols.map((c, ci) => (
            <Text
              key={ci}
              style={{
                width: colWidths[ci],
                marginRight: gap,
                fontFamily: "Roboto",
                fontSize: font,
                fontWeight: plain ? 600 : 400,
                color: c.pinned ? PINNED_COLOR : LABEL_COLOR,
                textAlign: "center",
                paddingBottom: 4 * scale,
              }}
            >
              {/* Non-breaking spaces so a borderline name never wraps to a clipped 2nd line. */}
              {plain ? c.label : c.label.replace(/ /g, String.fromCharCode(160))}
            </Text>
          ))}
        </View>
      )}

      {/* Body rows */}
      {visibleRows.map((row, ri) => (
        <View
          key={ri}
          style={{
            flexDirection: "row",
            height: cellH,
            marginTop: gap,
            flexShrink: 0,
            overflow: "hidden",
            ...(plain ? { borderBottomWidth: 0.5, borderBottomColor: "#f0f0f0" } : {}),
          }}
        >
          <View style={{ width: labelColWidth, justifyContent: "center", paddingLeft: ROW_LABEL_PAD_PX * scale }}>
            <Text style={{ fontFamily: "Roboto", fontSize: font, fontWeight: 400, color: row.pinned ? PINNED_COLOR : LABEL_COLOR }}>
              {row.label}
            </Text>
          </View>
          {row.cells.slice(0, visibleColCount).map((cell, ci) => (
            <View
              key={ci}
              style={{
                width: colWidths[ci],
                height: cellH,
                marginRight: gap,
                flexShrink: 0,
                ...(plain ? {} : { backgroundColor: cell.bg, borderRadius: 3 * scale }),
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {cell.label ? (
                <Text
                  style={{
                    width: colWidths[ci],
                    paddingHorizontal: plain ? 4 * scale : 0,
                    fontFamily: "Roboto",
                    fontSize: font,
                    fontWeight: cell.bold ? 600 : 400,
                    color: cell.color,
                    textAlign: "center",
                  }}
                >
                  {cell.label}
                </Text>
              ) : null}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}
